/**
 * Assignments — server state.
 *
 * Both mutations invalidate the volunteer keys as well as their own: an
 * assignment appears on the volunteer's profile too, and a roster change that
 * left that list stale would have staff reading two different answers to the same
 * question on two screens.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../components/common/Toast';
import {
  cancelAssignment,
  createAssignment,
  fetchActivityAssignments,
} from '../services/api/assignments';
import type { CreateAssignmentBody } from '../services/api/assignments';

import { assignmentKeys, volunteerKeys } from './queryKeys';
import { sessionKeys } from './useSessions';

export { assignmentKeys };

export function useActivityAssignments(activityId: string | undefined) {
  return useQuery({
    queryKey: assignmentKeys.forActivity(activityId ?? ''),
    queryFn: () => fetchActivityAssignments(activityId as string),
    enabled: Boolean(activityId),
    // Several coordinators may be building the same roster at once, so a stale
    // list here is actively misleading.
    staleTime: 0,
  });
}

export function useCreateAssignment(activityId: string) {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    // The session comes from the caller now — an activity can have several, so
    // this hook cannot know which one without being told. activityId is still
    // taken, but only to invalidate the right roster afterwards.
    mutationFn: (body: CreateAssignmentBody) => createAssignment(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: assignmentKeys.forActivity(activityId) });
      void queryClient.invalidateQueries({ queryKey: volunteerKeys.all });
      // A session's `assigned_count` (migration 043's capacity display) lives in
      // the session cache, not the roster's — without this, the picker keeps
      // showing the place as open until something else happens to refetch it.
      void queryClient.invalidateQueries({ queryKey: sessionKeys.all });
      toast.success('Volunteer assigned.');
    },
    onError: (error) => toast.error(error, 'Could not assign the volunteer.'),
  });
}

export function useCancelAssignment(activityId: string) {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (assignmentId: string) => cancelAssignment(assignmentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: assignmentKeys.forActivity(activityId) });
      void queryClient.invalidateQueries({ queryKey: volunteerKeys.all });
      void queryClient.invalidateQueries({ queryKey: sessionKeys.all });
      toast.success('Assignment removed.');
    },
    onError: (error) => toast.error(error, 'Could not remove the assignment.'),
  });
}
